import { createReducer, on } from '@ngrx/store';
import { Ticket } from '../models/ticket.model';

import * as ACTIONS from './account-tickets.actions';
import { TicketStatus } from '../models/ticket-status.model';

export const ACCOUNT_TICKETS_FEATURE_KEY = 'tickets';

export interface AccountTicketsState {
    all: Ticket[];
    accountTickets: Ticket[];
    deviceTickets: Ticket[];
    loaded: boolean;
    deviceTicketsLoaded?: boolean;
    page: number;
    pageSize: number;
    allPagesLoaded: boolean;
    offlinePagesLoaded: boolean;
    latestLoadedPage?: number;
}

export const ACCOUNT_TICKETS_INITIAL_STATE: AccountTicketsState = {
    all: [],
    accountTickets: [],
    deviceTickets: [],
    loaded: true,
    deviceTicketsLoaded: true,
    page: 0,
    pageSize: 10,
    allPagesLoaded: false,
    offlinePagesLoaded: false,
    latestLoadedPage: undefined
};

export const accountTicketsReducer = createReducer(
    ACCOUNT_TICKETS_INITIAL_STATE,
    on(ACTIONS.fetchAllAccountTickets, (state) => ({ ...state, loaded: false })),
    on(ACTIONS.fetchAllAccountTicketsSuccess, (state, { tickets }) => ({
        ...state,
        all: tickets,
        loaded: true
    })),
    on(ACTIONS.fetchAllAccountTicketsFailed, (state) => ({ ...state, loaded: true })),

    on(ACTIONS.fetchPaginatedAccountTickets, (state, { page, limit }) => {
        return {
            ...state,
            loaded: false,
            allPagesLoaded: false,
            offlinePagesLoaded: false,
            page,
            pageSize: limit
        };
    }),
    on(ACTIONS.fetchPaginatedAccountTicketsSuccess, (state, { tickets, page }) => {
        if (!tickets || tickets.length === 0) {
            return {
                ...state,
                loaded: true,
                allPagesLoaded: false,
                offlinePagesLoaded: true
            };
        }
        return {
            ...state,
            accountTickets: [...state.accountTickets, ...tickets],
            page,
            loaded: true,
            allPagesLoaded: tickets.length < state.pageSize,
            latestLoadedPage: state.page
        };
    }),
    on(ACTIONS.fetchPaginatedAccountTicketsFailed, (state) => ({ ...state, loaded: true })),
    on(ACTIONS.refetchAllAccountTicketPages, (state) => ({ ...state, loaded: false })),
    on(ACTIONS.refetchAllAccountTicketPagesSuccess, (state, { tickets }) => ({
        ...state,
        accountTickets: tickets,
        loaded: true
    })),
    on(ACTIONS.refetchAllAccountTicketPagesFailed, (state) => ({ ...state, loaded: true })),
    on(ACTIONS.fetchAccountTicketsHistory, (state) => ({ ...state, all: [], loaded: false })),
    on(ACTIONS.fetchAccountTicketsHistorySuccess, (state, { tickets }) => ({
        ...state,
        all: [...state.all, ...tickets],
        loaded: true
    })),
    on(ACTIONS.fetchAccountTicketsHistoryFailed, (state) => ({ ...state, all: [], loaded: true })),
    on(ACTIONS.activateTicket, (state) => ({ ...state, loaded: false })),
    on(ACTIONS.activateTicketSuccess, (state, payload) => ({
        ...state,
        loaded: true,
        deviceTicketsLoaded: true,
        all: state.all.map((ticket: Ticket) =>
            (ticket.docNum ?? ticket.id) === (payload.ticket.docNum ?? payload.ticket.id)
                ? {
                      ...ticket,
                      validated: true,
                      status: payload.ticket.status ?? TicketStatus.Changed,
                      starting: payload.ticket.starting,
                      ending: payload.ticket.ending,
                      units: payload.ticket.units
                  }
                : ticket
        ),
        deviceTickets: state.deviceTickets.map((ticket: Ticket) =>
            (ticket.docNum ?? ticket.id) === (payload.ticket.docNum ?? payload.ticket.id)
                ? {
                      ...ticket,
                      validated: true,
                      status: payload.ticket.status ?? TicketStatus.Changed,
                      starting: payload.ticket.starting,
                      ending: payload.ticket.ending,
                      units: payload.ticket.units
                  }
                : ticket
        ),
        accountTickets: state.accountTickets.map((ticket: Ticket) =>
            (ticket.docNum ?? ticket.id) === (payload.ticket.docNum ?? payload.ticket.id)
                ? {
                      ...ticket,
                      validated: true,
                      status: payload.ticket.status ?? TicketStatus.Changed,
                      starting: payload.ticket.starting,
                      ending: payload.ticket.ending,
                      units: payload.ticket.units
                  }
                : ticket
        )
    })),
    on(ACTIONS.reactivateTicketSuccess, (state, payload) => ({
        ...state,
        loaded: true,
        accountTickets: state.accountTickets.map((ticket: Ticket) =>
            ticket.docNum === String(payload.id) || ticket.id === payload.id
                ? {
                      ...ticket,
                      activated: true,
                      validated: true,
                      startDate: payload.startDate,
                      endDate: payload.endDate,
                      starting: payload.startDate,
                      ending: payload.endDate,
                      status: payload.status
                  }
                : ticket
        ),
        deviceTickets: state.deviceTickets.map((ticket: Ticket) =>
            ticket.docNum === String(payload.id) || ticket.id === payload.id
                ? {
                      ...ticket,
                      activated: true,
                      validated: true,
                      startDate: payload.startDate,
                      endDate: payload.endDate,
                      starting: payload.startDate,
                      ending: payload.endDate,
                      status: payload.status
                  }
                : ticket
        )
    })),
    on(ACTIONS.activateTicketFailed, (state) => ({ ...state, loaded: true })),
    on(ACTIONS.resetAccountTickets, (state) => ({
        ...state,
        all: [],
        accountTickets: [],
        deviceTickets: [],
        loaded: true,
        deviceTicketsLoaded: true,
        page: 0,
        pageSize: 10,
        allPagesLoaded: false,
        latestLoadedPage: undefined
    })),
    on(ACTIONS.fetchDeviceTickets, (state) => ({ ...state, deviceTicketsLoaded: false })),
    on(ACTIONS.fetchDeviceTicketsSuccess, (state, { tickets }) => ({
        ...state,
        deviceTickets: tickets,
        deviceTicketsLoaded: true,
        latestLoadedPage: -1
    })),
    on(ACTIONS.fetchDeviceTicketsFailed, (state) => ({ ...state, deviceTicketsLoaded: true })),
    on(ACTIONS.incrementPageIndex, (state, { incrementBy }) => ({
        ...state,
        page: state.page + incrementBy
    })),

    on(ACTIONS.resetState, () => ({
        ...ACCOUNT_TICKETS_INITIAL_STATE
    }))
);
